import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertComponent, CardSelectComponent, ModalComponent } from '@tecalisdev/infinity';
import { IIdentityBlockPayload } from '../../../identities/models/identities.models';
import { IProcessDetailResponse } from '../../../processes/models/processes.models';

@Component({
	selector: 'app-modal-unlock-identity',
	imports: [ReactiveFormsModule, FormsModule, ModalComponent, TranslatePipe, AlertComponent, CardSelectComponent],
	templateUrl: './modal-unlock-identity.component.html',
	styleUrl: './modal-unlock-identity.component.scss',
})
export class ModalUnlockIdentityComponent implements OnInit {
	@Input() id: string;
	@Input() locks?: IProcessDetailResponse['locks'];
	@Input() showOcrUnlockOption: boolean = true;
	@Input() showIpUnlockOption: boolean = true;
	@Input() showFaceUnlockOption: boolean = true;
	@Input() selectedOptions?: string[];
	@Output() closeEvent = new EventEmitter<undefined | Partial<IIdentityBlockPayload>>();

	private trans = inject(TranslateService);

	public errorMsg: string = '';
	public unlockedIdentityParams: string[] = [];
	public form = new FormGroup({
		ocr: new FormControl(false),
		ip: new FormControl(false),
		face: new FormControl(false),
	});

	ngOnInit(): void {
		this.getIdentityUnlocks();
	}

	private getIdentityUnlocks(): void {
		if (this.locks) {
			this.unlockedIdentityParams = [];
			if (
				this.showOcrUnlockOption &&
				(!this.locks?.blocked_identifiers || this.locks?.blocked_identifiers?.length === 0)
			) {
				this.unlockedIdentityParams.push('ocr');
			}
			if (!this.locks?.blocked_ips || this.locks?.blocked_ips?.length === 0) {
				this.unlockedIdentityParams.push('ip');
			}
			if (this.showFaceUnlockOption && (!this.locks?.blocked_faces || this.locks?.blocked_faces?.length === 0)) {
				this.unlockedIdentityParams.push('face');
			}
		}

		if (this.unlockedIdentityParams.length > 0) {
			this.form.patchValue({
				ocr: this.unlockedIdentityParams.includes('ocr'),
				ip: this.unlockedIdentityParams.includes('ip'),
				face: this.unlockedIdentityParams.includes('face'),
			});

			// Deshabilitar los checkbox que ya están bloqueados
			this.unlockedIdentityParams.forEach((block) => {
				this.form.get(block)?.disable();
			});
		}

		// Ignorar las opciones deshabilitadas, incluso si se pasaron opciones seleccionadas como input
		if (this.selectedOptions && this.selectedOptions.length > 0) {
			this.selectedOptions.forEach((option) => {
				const opt = this.form?.get(option);
				if (opt?.enabled) opt.setValue(true);
			});
		}
	}

	private getBody(): Partial<IIdentityBlockPayload> {
		const payload: Partial<IIdentityBlockPayload> = {};

		if (this.form.value.ip) {
			payload.blocked_ip = false;
		}

		if (this.form.value.ocr) {
			payload.blocked_identifier = false;
		}

		if (this.form.value.face) {
			payload.blocked_face = false;
		}

		return payload;
	}

	public closeModal(type: string) {
		if (type === 'buttonPrimary') {
			if (this.isAnyParameterSelected() === false) {
				this.errorMsg = this.trans.instant('PROCESSES.UNLOCK_IDENTITY_MODAL.AT_LEAST_ONE_PARAMETER_REQUIRED');
				return;
			}

			this.closeEvent.emit(this.getBody());
			return;
		}

		this.closeEvent.emit();
	}

	/**
	 * Verifica si al menos uno de los parámetros de bloqueo (OCR, IP o rostro) está seleccionado y no está deshabilitado.
	 *
	 * @returns {boolean} Verdadero si al menos un parámetro está seleccionado y habilitado, falso en caso contrario.
	 */
	private isAnyParameterSelected(): boolean {
		// Verificar si al menos un checkbox está seleccionado y no está deshabilitado
		const ocr = this.showOcrUnlockOption && this.form.get('ocr')?.value && !this.form.get('ocr')?.disabled;
		const ip =
			this.showIpUnlockOption && this.form.get('ip')?.value && !this.form.get('ip')?.disabled;
		const face = this.showFaceUnlockOption && this.form.get('face')?.value && !this.form.get('face')?.disabled;
		return ocr || ip || face;
	}
}
